import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { cancelFunctions } from 'utils/cancelFunctions';
import { Store } from 'components/common/types/Store.types';
import { deleteCreative, restoreCreative } from 'modules/api/content';
import { rejectCreative } from 'modules/api/previewContent';
import { addToastNotification } from 'store/notification/reducer';
import { ModalContext, useModal } from './useModalContext';

export type UsePreviewContentModals = {
  rejectModal: ModalContext;
  deleteModal: ModalContext;
  restoreModal: ModalContext;
  updateRejectReason: (reason: string) => void;
  rejectReason: string;
};

export const usePreviewContentModals = ({
  loadCreativesDetails,
}: {
  loadCreativesDetails: () => Promise<void>;
}): UsePreviewContentModals => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const marketId = useSelector((state: Store) => state.creative.marketId);
  const [rejectReason, setRejectReason] = useState('');

  const deleteModal = useModal(async (): Promise<void> => {
    try {
      await deleteCreative(cancelFunctions, marketId);
      loadCreativesDetails();
      deleteModal.close();
      dispatch(addToastNotification({ description: 'Creative Deleted', type: 'Success', timeout: 2000 }));
      // eslint-disable-next-line no-empty
    } catch {}
  });

  const rejectModal = useModal(async (): Promise<void> => {
    try {
      await rejectCreative(cancelFunctions, { marketId, rejectReason });
      rejectModal.close();
      dispatch(addToastNotification({ description: 'Creative Rejected', type: 'Success', timeout: 2000 }));
      navigate('/content-management/creatives');
      // eslint-disable-next-line no-empty
    } catch {}
  });

  const restoreModal = useModal(async (): Promise<void> => {
    try {
      await restoreCreative(cancelFunctions, marketId);
      loadCreativesDetails();
      restoreModal.close();
      dispatch(addToastNotification({ description: 'Creative Restored', type: 'Success', timeout: 2000 }));
      // eslint-disable-next-line no-empty
    } catch {}
  });

  const updateRejectReason = (reason?: string): void => {
    setRejectReason(reason || '');
  };

  return {
    deleteModal,
    rejectModal,
    restoreModal,
    updateRejectReason,
    rejectReason,
  };
};
